import { Checkbox, FormControlLabel, Paper } from '@mui/material';
import { map, orderBy } from 'lodash';
import React, { useCallback, useMemo } from 'react';

const CheckboxItem = React.memo<{
  itemId: string;
  label: string | undefined;
  checked: boolean;
  onToggle: (itemId: string, checked: boolean) => void;
}>(({ itemId, label, checked, onToggle }) => (
  <div>
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={(e) => onToggle(itemId, e.target.checked)}
          sx={{ paddingTop: 1, paddingBottom: 1 }}
        />
      }
      label={label}
    />
  </div>
));

type SelectionListProps<T extends { id: string; archived?: boolean }> = {
  title: string;
  items: T[];
  selectedIds: string[];
  sortByIds: string[];
  onSelectedIdsChange: React.Dispatch<React.SetStateAction<string[]>>;
  renderInput: () => React.ReactNode;
  labelGetter: (item: T) => string | undefined;
  noSelectionText: string;
};

function SelectionListInner<T extends { id: string; archived?: boolean }>({
  title,
  items,
  selectedIds,
  sortByIds,
  onSelectedIdsChange,
  renderInput,
  labelGetter,
  noSelectionText,
}: SelectionListProps<T>) {
  const sortedItems = useMemo(
    () =>
      orderBy(items, [
        (item) => !sortByIds.includes(item.id),
        (item) => item.archived,
        (item) => labelGetter(item),
      ]),
    // labelGetter is a pure function of item fields; items change drives resort
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, sortByIds],
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleToggle = useCallback(
    (itemId: string, checked: boolean) => {
      onSelectedIdsChange((prev) =>
        checked ? [...prev, itemId] : prev.filter((sId) => sId !== itemId),
      );
    },
    [onSelectedIdsChange],
  );

  return (
    <div className="tw-flex-1 tw-flex tw-flex-col">
      <div className="tw-text-xl tw-mb-4">{`${title} (${selectedIds.length})`}</div>
      {renderInput()}
      <Paper
        elevation={0}
        sx={{ padding: 3, paddingX: 3 }}
        className="md:tw-flex-1"
      >
        {!items?.length ? (
          <div>{noSelectionText}</div>
        ) : (
          <div className="tw-max-h-40 tw-overflow-auto">
            {map(sortedItems, (item) => (
              <CheckboxItem
                key={item.id}
                itemId={item.id}
                label={labelGetter(item)}
                checked={selectedSet.has(item.id)}
                onToggle={handleToggle}
              />
            ))}
          </div>
        )}
      </Paper>
    </div>
  );
}

export const SelectionList = React.memo(
  SelectionListInner,
) as typeof SelectionListInner;
